import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, CardHeader, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { fNumber } from '../../../utils/formatNumber';
import i18n from '../../../i18n';

// ----------------------------------------------------------------------

// What a status means decides its colour, so success stays green on every
// dashboard; the old donut took colours by position and gave the same status a
// different one whenever the set of statuses changed.
const TONE = {
    success: ['PROCESSED', 'processed', 'payment_success', 'payment_final'],
    error: ['REFUSED', 'refused', 'error', 'fraud', 'paymentRefused', 'payoutRefused'],
    neutral: ['expired', 'cancelled'],
    info: ['REFUND']
};
const toneOf = (status) =>
    Object.keys(TONE).find((tone) => TONE[tone].includes(status)) || 'warning';

// Several statuses can share a tone; they step down in strength from its main
// colour. The ramp's light and dark steps sit too close to main in one theme or
// the other to tell apart.
const STRENGTH = [1, 0.6, 0.35, 0.2];
const colorFor = (theme, tone, index) => {
    const base = tone === 'neutral' ? theme.palette.grey[500] : theme.palette[tone].main;
    const strength = STRENGTH[index % STRENGTH.length];
    return strength === 1 ? base : alpha(base, strength);
};

/**
 * Orders by status as one bar of shares over a legend list (status, count,
 * share). The donut it replaces sat in a quarter-width column, where its
 * slices and floating legend were hard to read.
 */
export default function DashboardStatusBreakdown({ title, data, loading }) {
    const rows = data.filter((row) => row.value > 0).sort((a, b) => b.value - a.value);
    const total = rows.reduce((sum, row) => sum + row.value, 0);

    const seen = {};
    const items = rows.map((row) => {
        const tone = toneOf(row.label);
        seen[tone] = (seen[tone] ?? -1) + 1;
        return {
            ...row,
            name: i18n.t(`dashboard.${row.label}`, { defaultValue: row.label }),
            color: (theme) => colorFor(theme, tone, seen[tone]),
            share: total ? Math.round((row.value / total) * 1000) / 10 : 0
        };
    });

    let content;
    if (loading) {
        content = (
            <Stack spacing={2}>
                <Skeleton variant="rounded" height={12} />
                {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="text" height={22} />
                ))}
            </Stack>
        );
    } else if (!total) {
        content = (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {i18n.t('common.noData')}
            </Typography>
        );
    } else {
        content = (
            <Stack spacing={2.5}>
                <Box
                    sx={{
                        display: 'flex',
                        height: 12,
                        borderRadius: 6,
                        overflow: 'hidden',
                        gap: '2px',
                        bgcolor: 'background.neutral'
                    }}
                >
                    {items.map((item) => (
                        <Tooltip key={item.label} title={`${item.name}: ${fNumber(item.value)}`}>
                            <Box sx={{ flex: `${item.value} 0 0`, minWidth: 3, bgcolor: item.color }} />
                        </Tooltip>
                    ))}
                </Box>

                <Stack component="ul" spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {items.map((item) => (
                        <Stack
                            component="li"
                            key={item.label}
                            direction="row"
                            alignItems="center"
                            spacing={1.25}
                        >
                            <Box
                                aria-hidden
                                sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, bgcolor: item.color }}
                            />
                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                {item.name}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {fNumber(item.value)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ width: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                            >
                                {item.share}%
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        );
    }

    return (
        <Card sx={{ height: '100%' }}>
            <CardHeader title={title} />
            <Box sx={{ p: 3 }}>{content}</Box>
        </Card>
    );
}

DashboardStatusBreakdown.propTypes = {
    title: PropTypes.string,
    /** `{ label: status, value: count }` per status. */
    data: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })),
    loading: PropTypes.bool
};

DashboardStatusBreakdown.defaultProps = { data: [] };
